package com.iptvplayer.web.ui.screen

import androidx.compose.runtime.*
import com.iptvplayer.shared.domain.model.ContentType
import com.iptvplayer.shared.presentation.viewmodel.BrowsePagingViewModel
import com.iptvplayer.shared.presentation.viewmodel.BrowsePagingViewModel.BrowseType
import com.iptvplayer.web.ui.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Browse, backed by [BrowsePagingViewModel]: the page renders one *window* of
 * results at a time (60 rows, growing via "Show more") instead of dumping up to
 * 200 cards per type into the DOM, and search/category filtering runs in the
 * shared windowed pipeline (debounced) instead of a per-keystroke linear scan.
 */
@Composable
fun BrowseScreen(vm: BrowsePagingViewModel, onPlay: OnPlay, onSeriesOpen: OnSeriesOpen, onOpenSources: () -> Unit = {}) {
    val type by vm.selectedType.collectAsState()
    val search by vm.searchInput.collectAsState()
    val selectedCategory by vm.selectedCategory.collectAsState()
    val isSyncing by vm.isSyncing.collectAsState()
    val issue by vm.issue.collectAsState()

    // Tab badges: stable catalog totals (don't change while typing).
    val liveCount by vm.liveCount.collectAsState()
    val moviesCount by vm.moviesCount.collectAsState()
    val seriesCount by vm.seriesCount.collectAsState()

    // Active window + filtered total for the selected type.
    val channels by vm.channels.collectAsState()
    val channelTotal by vm.channelTotal.collectAsState()
    val vod by vm.vod.collectAsState()
    val vodTotal by vm.vodTotal.collectAsState()
    val series by vm.series.collectAsState()
    val seriesTotal by vm.seriesTotal.collectAsState()

    val categories by when (type) {
        BrowseType.MOVIES -> vm.vodCategories
        BrowseType.SERIES -> vm.seriesCategories
        BrowseType.LIVE -> vm.channelCategories
    }.collectAsState()

    val activeTotal = when (type) {
        BrowseType.MOVIES -> vodTotal
        BrowseType.SERIES -> seriesTotal
        BrowseType.LIVE -> channelTotal
    }

    PageContainer {
        issue?.let { current ->
            IssueBanner(current, onRetry = { vm.refresh() }, onOpenSources = onOpenSources)
        }

        Field("Search", search, "Search live, movies or series") { vm.setSearch(it) }

        // ── Live / Movies / Series tabs ──
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); property("gap", "10px"); property("flex-wrap", "wrap"); property("margin-bottom", "14px")
            }
        }) {
            TypeTab("📺 Live", liveCount, type == BrowseType.LIVE) { vm.selectType(BrowseType.LIVE) }
            TypeTab("🎬 Movies", moviesCount, type == BrowseType.MOVIES) { vm.selectType(BrowseType.MOVIES) }
            TypeTab("📽 Series", seriesCount, type == BrowseType.SERIES) { vm.selectType(BrowseType.SERIES) }
        }

        // ── Sub-categories within the active type ──
        if (categories.isNotEmpty()) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); property("flex-wrap", "wrap"); property("gap", "8px"); property("margin-bottom", "12px")
                }
            }) {
                Chip("All", selectedCategory == null) { vm.selectCategory(null) }
                categories.forEach { cat ->
                    Chip(cat, selectedCategory == cat) { vm.selectCategory(cat) }
                }
            }
        }

        if (activeTotal == 0L) {
            when {
                isSyncing -> Spinner("Syncing content from your provider…")
                issue != null -> EmptyState("Nothing to show — see the message above.")
                else -> EmptyState(
                    when (type) {
                        BrowseType.MOVIES -> "No movies match your filters."
                        BrowseType.SERIES -> "No series match your filters."
                        BrowseType.LIVE -> "No live channels match your filters."
                    }
                )
            }
            return@PageContainer
        }

        when (type) {
            BrowseType.LIVE -> {
                SectionTitle("Live channels ($channelTotal)")
                MediaGrid {
                    channels.forEach { c ->
                        MediaCard(c.name, c.logoUrl, c.group) {
                            onPlay(c.id, c.name, c.streamUrl, ContentType.CHANNEL, c.logoUrl)
                        }
                    }
                }
                LoadMoreRow(channels.size.toLong(), channelTotal) { vm.loadMoreChannels() }
            }
            BrowseType.MOVIES -> {
                SectionTitle("Movies ($vodTotal)")
                MediaGrid {
                    vod.forEach { v ->
                        MediaCard(v.name, v.coverUrl, v.genre) {
                            onPlay(v.id, v.name, v.streamUrl, ContentType.VOD, v.coverUrl)
                        }
                    }
                }
                LoadMoreRow(vod.size.toLong(), vodTotal) { vm.loadMoreVod() }
            }
            BrowseType.SERIES -> {
                SectionTitle("Series & shows ($seriesTotal)")
                MediaGrid {
                    series.forEach { s ->
                        MediaCard(s.name, s.posterUrl, s.genre) {
                            onSeriesOpen(s.sourceId, s.seriesId, s.name)
                        }
                    }
                }
                LoadMoreRow(series.size.toLong(), seriesTotal) { vm.loadMoreSeries() }
            }
        }
    }
}

@Composable
private fun LoadMoreRow(shown: Long, total: Long, onLoadMore: () -> Unit) {
    if (shown >= total) return
    Div(attrs = {
        style {
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center)
            property("gap", "8px"); property("margin", "18px 0 6px")
        }
    }) {
        Div(attrs = {
            style { property("color", Theme.textDim); property("font-size", "13px") }
        }) { Text("Showing $shown of $total") }
        Button(attrs = {
            onClick { onLoadMore() }
            style {
                property("padding", "10px 26px"); property("border-radius", "12px")
                property("border", "1px solid ${Theme.border}"); property("background", Theme.surface)
                property("color", Theme.text); property("font-size", "14px"); property("font-weight", "700")
                property("cursor", "pointer")
                property("transition", "border-color .15s ease, transform .12s ease")
            }
        }) { Text("Show more") }
    }
}

@Composable
private fun TypeTab(label: String, count: Long, active: Boolean, onClick: () -> Unit) {
    Button(attrs = {
        onClick { onClick() }
        style {
            property("padding", "10px 18px"); property("border-radius", "12px"); property("border", "1px solid ${if (active) "#7c3aed" else Theme.border}")
            property("background", if (active) "linear-gradient(90deg, #7c3aed, #4f8cff)" else Theme.surface)
            property("color", Theme.text); property("font-size", "14px"); property("font-weight", "700"); property("cursor", "pointer")
            property("transition", "border-color .15s ease, background .15s ease, transform .12s ease")
            if (active) property("box-shadow", "0 4px 14px rgba(124,58,237,.35)")
        }
    }) {
        Text(label)
        if (count > 0) {
            Span(attrs = {
                style {
                    property("margin-left", "8px"); property("padding", "2px 7px"); property("border-radius", "999px")
                    property("background", if (active) "rgba(255,255,255,.18)" else "rgba(255,255,255,.06)")
                    property("font-size", "12px"); property("font-weight", "700")
                }
            }) { Text(count.toString()) }
        }
    }
}
